<template>
    <CqModal
        :showModal="show"
        @closeModal="closeModal"
        heading="Wensen"
        :busy="isSaving"
        :preventClosing="isSaving"
    >
        <template v-if="wish">
            <InfoGrid>
                <CqToggle class="infoGridInformationWithoutHeader" v-model="weeklyRecurring" @update:modelValue="loadWish" :readonly="wishExists" label="Wekelijks herhalend" />
                <InfoHeader>Werknemer</InfoHeader>
                <span>{{ resourceDisplayName }}</span>
                <InfoHeader>Datum</InfoHeader>
                <div v-if="wish.recursWeekly">Wekelijks herhalend op {{ dayName }}</div>
                <div v-else>{{ formattedDate }}</div>
                <CqTextInput class="infoGridInformationWithoutHeader" label="Opmerking" :maxlength="1000" v-model.trim="wish.description" />
            </InfoGrid>

            <div class="details">
                <CqToggle v-model="doNotWantToWork" @update:modelValue="setBeginAndEndDoesntWantToWork" label="Wil niet werken" />
                <CqSelectInput label="Wil niet werken vanaf" :options="dateTimeSpecificationOptionsWishes" v-model="doesntWantToWorkBeginTime" @update:modelValue="updateWishNotToWorkTimes" noMargin />
                <CqSelectInput label="Wil niet werken tot" :options="dateTimeSpecificationOptionsWishes" v-model="doesntWantToWorkEndTime" @update:modelValue="updateWishNotToWorkTimes" noMargin />
            </div>

            <div class="details">
                <CqSelectInput label="Wil dienst" :options="filteredShiftsCurrentRoster" :modelValue="wish.wantsShiftId1 ?? undefined" @update:modelValue="wish.wantsShiftId1 = $event ? Number($event) : null" noMargin />
                <CqSelectInput v-if="wish.wantsShiftId1" label="of dienst" :options="filteredShiftsCurrentRoster" :modelValue="wish.wantsShiftId2 ?? undefined" @update:modelValue="wish.wantsShiftId2 = $event ? Number($event) : null" noMargin />
                <CqSelectInput v-if="wish.wantsShiftId2" label="of dienst" :options="filteredShiftsCurrentRoster" :modelValue="wish.wantsShiftId3 ?? undefined" @update:modelValue="wish.wantsShiftId3 = $event ? Number($event) : null" noMargin />
            </div>
        </template>

        <CqButtonBar>
            <CqButton v-if="wishForThisDayAvailable" :disabled="isSaving" class="no-shadow on-left" @click="removeWish" dangerous label="Verwijder wens" />
            <CqButton v-if="wishForThisDayAvailable" :disabled="isSaving || !changeActive" class="no-shadow" @click="changeWish" primary label="Pas wens aan" />
            <CqButton v-else :disabled="isSaving || !changeActive" class="no-shadow" @click="saveWish" primary label="Sla wens op" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqModal, CqSelectInput, CqTextInput, CqToggle, StringSelectInputOption, NumberSelectInputOption, NullSelectInputOption} from '@cquential/ui';
    import Notify from '@/components/modals/Notify.vue';
    import {ApiClient, PersonalPlanningWish, Shift, ApiError} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import isEqual from 'lodash/isEqual';
    import cloneDeep from 'lodash/cloneDeep';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import {PlanbordResource} from '@/types/api-object-extensions';
    import {Component, Vue, Prop, Watch} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqSelectInput,
            CqTextInput,
            CqToggle,
            InfoGrid,
            InfoHeader,
        },
        emits: [
            'create:wish',
            'update:wish',
            'delete:wish',
        ],
    })
    export default class Wishes extends Vue {
        private static instance: Wishes | null = null;

        @Prop({required: true}) public shiftIds!: number[];
        @Prop({required: true}) public shifts!: Record<number, Shift>;

        private resource: PlanbordResource | null = null;
        private date: Date = new Date();
        private doesntWantToWorkBeginTime: string | undefined = undefined;
        private doesntWantToWorkEndTime: string | undefined = undefined;
        private weeklyRecurring: boolean = false;
        private wish: PersonalPlanningWish = {
            doesntWantToWorkBegin: null,
            doesntWantToWorkEnd: null,
            date: null,
            recursWeekly: true,
            recursOnDay: 1,
            description: '',
            resourceId: 0,
            wantsShiftId1: null,
            wantsShiftId2: null,
            wantsShiftId3: null,
        };

        private wishClone: PersonalPlanningWish | null = null;
        private changeActive: boolean = false;
        private isSaving: boolean = false;
        private dateTimeSpecificationOptionsWishes: StringSelectInputOption[] = [];
        private wishForThisDayAvailable: boolean = false;
        private doNotWantToWork: boolean = false;
        private defaultShiftId: number = 0;
        private show: boolean = false;

        public get resourceDisplayName(): string {
            return this.resource ? ResourceUtil.displayName(this.resource) : '';
        }

        @Watch('wish.wantsShiftId1')
        public onWantsShiftId1Change(newValue: number | null): void {
            this.onChangeWantsShiftIds(newValue);
        }

        @Watch('wish.wantsShiftId2')
        public onWantsShiftId2Change(newValue: number | null): void {
            this.onChangeWantsShiftIds(newValue);
        }

        @Watch('wish.wantsShiftId3')
        public onWantsShiftId3Change(newValue: number | null): void {
            this.onChangeWantsShiftIds(newValue);
        }

        @Watch('wish', {deep: true})
        public onWishChange(newValue: PersonalPlanningWish): void {
            this.onChangePersonalPlanningWishesModel(newValue);
        }

        public mounted(): void {
            Wishes.instance = this;
        }

        public beforeUnmount(): void {
            Wishes.instance = null;
        }

        public get dayName(): string {
            return Dates.Utils.DAY_NAMES[Dates.Utils.getDay(this.date)];
        }

        public get formattedDate(): string {
            return Dates.Formatter.formatDateHumanFriendly(this.date);
        }

        public get wishExists(): boolean {
            return Boolean(this.wish.id);
        }

        public get filteredShiftsCurrentRoster(): [NullSelectInputOption, ...NumberSelectInputOption[]] {
            // Define array to return
            const filteredShifts: NumberSelectInputOption[] = [];
            // Created date object of given date
            // Loop
            for (const shiftId of this.shiftIds) {
                // set default shiftId for this roster
                if (this.shifts[shiftId].template!.isDefault) {
                    this.defaultShiftId = Number(shiftId);
                }
                // Check is begin and enddate are within range
                if (this.date >= this.shifts[shiftId].beginDate && this.date <= this.shifts[shiftId].endDate) {
                    filteredShifts.push({
                        label: this.shifts[shiftId].template!.code,
                        value: Number(shiftId),
                    });
                }
            }
            // Sort array alphabetically by value
            filteredShifts.sort((a: NumberSelectInputOption, b: NumberSelectInputOption): number => a.label.localeCompare(b.label));

            // Return filtered array of objects
            return [{value: null, label: '(geen)'}, ...filteredShifts];
        }

        public buildDateTimeSpecificationOptionsWishes(): void {
            // Define placeholder as returnvalue
            this.dateTimeSpecificationOptionsWishes = [];
            const begin: Date = this.weeklyRecurring ? this.getDefaultBeginDate() : new Date(this.date);
            const end: Date = Dates.Utils.addDays(begin, 1);
            end.setTime(end.getTime() + 1);

            const precision: number = 15;

            for (let i: number = 1; begin < end; i++) {
                this.dateTimeSpecificationOptionsWishes.push({
                    // Add plus +1 if time is on the next day
                    label: ((i * precision) > 1440) ? Dates.Formatter.formatTime(begin) + ' (+1)' : Dates.Formatter.formatTime(begin),
                    // Value is stamp
                    value: begin.toISOString(),
                });
                // Add 15 minutes
                begin.setMinutes(begin.getMinutes() + precision);
                // Plus up iterator
            }

            this.doesntWantToWorkBeginTime = String(this.dateTimeSpecificationOptionsWishes[0].value);
            this.doesntWantToWorkEndTime = String(this.dateTimeSpecificationOptionsWishes[this.dateTimeSpecificationOptionsWishes.length - 1].value);
        }

        public getDefaultBeginDate(): Date {
            const date: Date = new Date('1970-01-01');
            date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

            return date;
        }

        public loadWish(): void {
            // Generate data
            this.resetPersonalPlanningWishesModel();
            this.buildDateTimeSpecificationOptionsWishes();
            this.initializePersonalPlanningWishes();
            this.cloneModelAndResetChangeActive();
        }

        // Initialize
        public initializePersonalPlanningWishes(): void {
            this.doNotWantToWork = true;
            if (this.resource && this.resource.personalPlanningWishes && this.resource.personalPlanningWishes.length > 0) {
                this.setPersonalPlanningWishes();
            } else {
                this.resetPersonalPlanningWishesModel();
            }
        }

        // Set initial wish
        public setPersonalPlanningWishes(): void {
            if (this.resource && this.resource.personalPlanningWishes) {
                for (const personalPlanningWish of this.resource.personalPlanningWishes) {
                    if (
                        // recursOnDay is -1 when recursWeekly is false (-1 != [0-7])
                        (personalPlanningWish.recursWeekly && personalPlanningWish.recursOnDay % 7 === Dates.Utils.getDay(this.date))
                        || (!personalPlanningWish.recursWeekly && personalPlanningWish.date?.valueOf() === this.date.valueOf())
                    ) {
                        this.wish = cloneDeep(personalPlanningWish);
                        this.weeklyRecurring = personalPlanningWish.recursWeekly;
                        this.doesntWantToWorkBeginTime = (personalPlanningWish.doesntWantToWorkBegin === null) ? undefined : personalPlanningWish.doesntWantToWorkBegin.toISOString();
                        this.doesntWantToWorkEndTime = (personalPlanningWish.doesntWantToWorkEnd === null) ? undefined : personalPlanningWish.doesntWantToWorkEnd.toISOString();
                        this.wishForThisDayAvailable = true;
                        this.doNotWantToWork = personalPlanningWish.doesntWantToWorkEnd !== null;
                        break;
                    }
                }
            }
        }

        // Create wish
        public saveWish(): void {
            if (this.resource && this.resource.id) {
                // Set checkbox to false when both begin and end are null
                if (this.doesntWantToWorkBeginTime === undefined && this.doesntWantToWorkEndTime === undefined) {
                    this.doNotWantToWork = false;
                }

                this.wish.doesntWantToWorkBegin = this.doesntWantToWorkBeginTime === undefined ? null : new Date(this.doesntWantToWorkBeginTime!);
                this.wish.doesntWantToWorkEnd = this.doesntWantToWorkBeginTime === undefined ? null : new Date(this.doesntWantToWorkEndTime!);

                this.createWish(this.wish)
                    .then((wishId: number): void => {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'De wens is opgeslagen.', type: 'success'});

                        // Set data to update roster
                        this.wish.id = wishId;

                        this.wishForThisDayAvailable = true;
                        // Dispatch action to update the local roster on success from API
                        this.$emit('create:wish', this.wish);
                        this.cloneModelAndResetChangeActive();
                    })
                    .catch((error: ApiError): void => {
                        // Loop trough error object and display in notifies
                        this.handleErrors(error);
                    });
            } else {
                throw new Error('No resource in component');
            }
        }

        // Update wish
        public changeWish(): void {
            if (this.resource && this.resource.id) {
                // Set checkbox to false when both begin and end are null
                if (this.doesntWantToWorkBeginTime === undefined && this.doesntWantToWorkEndTime === undefined) {
                    this.doNotWantToWork = false;
                }

                this.wish.doesntWantToWorkBegin = this.doesntWantToWorkBeginTime === undefined ? null : new Date(this.doesntWantToWorkBeginTime!);
                this.wish.doesntWantToWorkEnd = this.doesntWantToWorkBeginTime === undefined ? null : new Date(this.doesntWantToWorkEndTime!);

                this.updateWish(this.wish).then((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'De wens is aangepast.', type: 'success'});
                    // Dispatch action to update the local roster on success from API
                    this.$emit('update:wish', this.wish);
                    this.cloneModelAndResetChangeActive();
                }).catch((error: ApiError): void => {
                    // Loop trough error object and display in notifies
                    this.handleErrors(error);
                });
            } else {
                throw new Error('No resource in component');
            }
        }

        // Remove wish
        public removeWish(): void {
            if (!this.wish.id) {
                throw new Error('Cannot remove wish that has no ID.');
            }

            this.deleteWish(this.wish.id)
                .then((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'De wens is verwijderd.', type: 'success'});
                    // Dispatch action to update the local roster on success from API
                    this.$emit('delete:wish', this.wish);
                    this.resetPersonalPlanningWishesModel();
                    this.cloneModelAndResetChangeActive();
                    this.doNotWantToWork = true;
                });
        }

        // Reset values
        public resetPersonalPlanningWishesModel(): void {
            if (!this.resource || !this.resource.id) {
                throw new Error('Cannot edit wish for a non-persisted resource!');
            }

            this.wish = {
                doesntWantToWorkBegin: null,
                doesntWantToWorkEnd: null,
                date: this.weeklyRecurring ? null : this.date,
                recursWeekly: this.weeklyRecurring,
                recursOnDay: Dates.Utils.getDay(this.date),
                description: '',
                resourceId: this.resource.id,
                wantsShiftId1: null,
                wantsShiftId2: null,
                wantsShiftId3: null,
            };

            if (this.wish.recursOnDay === 0) {
                // ISO Sunday = 7, but .getDay() will return 0
                this.wish.recursOnDay = 7;
            }

            this.wishForThisDayAvailable = false;
        }

        // Set begin and end doesntwanttowork
        public setBeginAndEndDoesntWantToWork(): void {
            if (this.doNotWantToWork) {
                this.doesntWantToWorkBeginTime = String(this.dateTimeSpecificationOptionsWishes[0].value);
                this.doesntWantToWorkEndTime = String(this.dateTimeSpecificationOptionsWishes[this.dateTimeSpecificationOptionsWishes.length - 1].value);
            } else {
                this.doesntWantToWorkBeginTime = undefined;
                this.doesntWantToWorkEndTime = undefined;
            }
        }

        // Method to clone model to non reactive and set change active to false
        public cloneModelAndResetChangeActive(): void {
            this.wishClone = cloneDeep(this.wish);
            this.changeActive = false;
        }

        // Set change active to true when model and modelplaceholder differ
        public setChangeActive(origin: PersonalPlanningWish, placeholder?: PersonalPlanningWish): boolean {
            if (placeholder === undefined) {
                throw new Error('Wish clone was never created. Cannot check for changes.');
            }

            return !isEqual(origin, placeholder);
        }

        public updateWishNotToWorkTimes(): void {
            this.wish.doesntWantToWorkBegin = this.doesntWantToWorkBeginTime === undefined ? null : new Date(this.doesntWantToWorkBeginTime);
            this.wish.doesntWantToWorkEnd = this.doesntWantToWorkEndTime === undefined ? null : new Date(this.doesntWantToWorkEndTime);
        }

        public onChangePersonalPlanningWishesModel(val: PersonalPlanningWish): void {
            this.changeActive = this.setChangeActive(val, this.wishClone ?? undefined);
        }

        public onChangeWantsShiftIds(shiftId: number | null): void {
            if (shiftId === null) {
                this.shiftWishes();
            }
        }

        // if the user input creates "holes" in the wishes (e.g. 3 is set and 2 is not), we shift everything "up" (e.g. 2 will be set, and 3 won't be)
        public shiftWishes(): void {
            let wishes: Array<number | null> = [
                this.wish.wantsShiftId1,
                this.wish.wantsShiftId2,
                this.wish.wantsShiftId3,
            ];
            wishes = wishes.filter((value: number | null): value is number => value !== null);
            this.wish.wantsShiftId1 = wishes.length > 0 ? wishes[0] : null;
            this.wish.wantsShiftId2 = wishes.length > 1 ? wishes[1] : null;
            this.wish.wantsShiftId3 = wishes.length > 2 ? wishes[2] : null;
        }

        public createWish(wish: PersonalPlanningWish): Promise<number> {
            this.isSaving = true;

            // Make call to update deployment
            return ApiClient.instance.saveNewWish(wish).finally((): false => this.isSaving = false);
        }

        public updateWish(wish: PersonalPlanningWish): Promise<void> {
            this.isSaving = true;

            // Make call to update deployment
            return ApiClient.instance.updateWish(wish).finally((): false => this.isSaving = false);
        }

        public deleteWish(wishId: number): Promise<void> {
            this.isSaving = true;

            // Make call to update deployment
            return ApiClient.instance.deleteWish(wishId).finally((): false => this.isSaving = false)
                // Error
                .catch((error: ApiError): void => {
                    // Loop trough error object and display in notifies
                    this.handleErrors(error);
                });
        }

        public handleErrors(error?: ApiError): void {
            if (error?.response?.data) {
                for (const errorField in error.response.data.errors) {
                    for (const message of error.response.data.errors[errorField]) {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: message, type: 'error'});
                    }
                }
            } else {
                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Onbekende fout.', type: 'error'});
            }
        }

        public closeModal(): void {
            this.show = false;
        }

        public static openWishes(date: Date, resource: PlanbordResource): void {
            this.instance!.resource = resource;
            this.instance!.date = date;
            this.instance!.wishClone = null;
            this.instance!.changeActive = false;
            this.instance!.weeklyRecurring = false;

            this.instance!.loadWish();
            this.instance!.show = true;
        }
    }
</script>

<style lang="scss" scoped>
    .infoGridInformationWithoutHeader {
        grid-column: span 2;
    }

    .details {
        padding: 1rem;
        border-radius: 3px;
        background: var(--wish-information-background);
        display: grid;
        gap: 1rem;

        + .details {
            margin-top: 0.75rem;
        }
    }
</style>
